import { ProblemCode } from "@syla/shared/errors/ProblemCode";
import {
  ImportMethod,
  ExceededLimitType,
} from "@syla/shared/errors/problems/ExceededRecordLimitProblem";
import { LockSubject } from "@syla/shared/errors/problems/ResourceLockedData";
import { Resource } from "@syla/shared/errors/problems/ResourceNotFoundProblem";
import { ValidationErrorData } from "@syla/shared/errors/problems/ValidationProblem";
import {
  isSylaApiError,
  isSylaApiErrorCode,
} from "@syla/shared/errors/SylaApiError";
import { formatStringWithCommas } from "@syla/shared/helpers/formatting";
import { assertNever } from "@syla/shared/types/helpers/errors";
import { getPermissionDeniedText } from "@syla/shared/types/helpers/permissions";
import { AccountPermission } from "@syla/shared/types/models/AccountAccessBase";
import axios from "axios";
import { UnexpectedErrorString } from "../../../components/atoms/UnexpectedError";
import { UserErrorMessage } from "../UserErrorMessage";
import { parseSyncErrorMessage } from "./parseSyncErrorMessage";

/**
 * Convert an Error to an understandable message which can be safely
 * displayed to the user.
 */
export function parseErrorToUserMessage(error: unknown): UserErrorMessage {
  return (
    parseValidationError(error) ??
    parseUserAccountPermissionDeniedError(error) ??
    parseFeatureAccessDeniedError(error) ??
    parseResourceNotFoundError(error) ??
    parseExceededRecordLimitError(error) ??
    parseInvalidCustomFileTemplateError(error) ??
    parseOtherSylaApiError(error) ??
    parseLegacyFallback(error) ??
    parseValidateKeysFailedError(error) ??
    parseResourceLockedError(error) ??
    parseTransactionInvalidError(error) ??
    DefaultUserErrorMessage
  );
}

function parseValidationError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.ValidationFailed)) {
    const message =
      error.data.problems.map(parseValidationFieldError).join(", ") + ".";

    return {
      description: message,
    };
  }

  return undefined;
}

function parseInvalidCustomFileTemplateError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.InvalidCustomFileTemplate)) {
    // this is a carry-over from a legacy implementation. The user-facing
    // message has already been built in the b/e.
    const message = error.data.userMessage;

    return {
      description: message,
    };
  }

  return undefined;
}

function parseResourceLockedError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.ResourceLocked)) {
    const message =
      error.data.subjectType === LockSubject.ApiKey ?
        `There is already a sync in progress for this API Key. Please try again later or remove the duplicate key.`
      : `The Data Source is currently in use. Please try again later.`;

    return {
      description: message,
    };
  }

  return undefined;
}

function parseValidateKeysFailedError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.ValidateKeysFailed)) {
    const props = parseSyncErrorMessage(
      error.data.syncAuthType,
      error.data.details,
      error.data.dataSourceType,
    );
    return {
      title: props.title,
      description: props.message,
    };
  }

  return undefined;
}
function parseTransactionInvalidError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.TransactionInvalid)) {
    return {
      description:
        error.data.userMessage ||
        "We couldn’t save your changes because the resulting transaction would be invalid.",
    };
  }

  return undefined;
}

function parseValidationFieldError(error: ValidationErrorData) {
  const code = error.code;
  if (code === ProblemCode.FieldRequired) {
    return `${error.field} is required`;
  } else if (code === ProblemCode.FileRequired) {
    return `At least one file must be attached`;
  } else if (code === ProblemCode.UnsupportedFileType) {
    return `Some files types are not supported. Allowed types: ${error.allowedTypes.join(", ")}`;
  } else {
    assertNever(code);
  }
}

function parseUserAccountPermissionDeniedError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.UserAccountPermissionDenied)) {
    if (error.data.permission === AccountPermission.ChangeAccess) {
      return {
        description: `You don't have permission to change who has access to this account.`,
      };
    } else if (error.data.permission === AccountPermission.ChangeOwnership) {
      return {
        description: `You don't have permission to change the Owner on this account.`,
      };
    } else if (error.data.permission === AccountPermission.DeleteAccount) {
      return {
        description: `You don't have permission to delete this account.`,
      };
    } else if (error.data.permission === AccountPermission.ChangeTaxSettings) {
      return {
        description: `You don't have permission to change the tax settings on this account.`,
      };
    } else if (
      error.data.permission === AccountPermission.ChangeAccountDetails
    ) {
      return {
        description: `You don't have permission to change details on this account.`,
      };
    } else if (error.data.permission === AccountPermission.ManageSubscription) {
      return {
        description: `The user must retain access to manage the subscription.`,
      };
    } else if (
      error.data.permission === AccountPermission.PurchaseSubscription
    ) {
      return {
        description: `You don't have access to purchase a subscription for this account.`,
      };
    }
  }

  return undefined;
}

function parseFeatureAccessDeniedError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.AccountFeatureAccessDenied)) {
    const message = getPermissionDeniedText(error.data);
    if (message) {
      return {
        description: message,
      };
    }
  }

  return undefined;
}

function parseResourceNotFoundError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.ResourceNotFound)) {
    const map: Partial<Record<Resource, string>> = {
      StripeCustomer: "There is no Billing account linked to this user.",
      Wallet: "The Data Source could not be found.",
    };

    return {
      description:
        map[error.data.resource] ??
        `The ${error.data.resource} could not be found.`,
    };
  }

  return undefined;
}

function parseExceededRecordLimitError(error: unknown) {
  if (isSylaApiErrorCode(error, ProblemCode.ExceededRecordLimit)) {
    const importMethod = error.data.importMethod;

    const actualCount = formatStringWithCommas(
      error.data.actualCount.toString(),
    );

    const maxCountAllowed = formatStringWithCommas(
      error.data.maxCountAllowed.toString(),
    );

    const limitTypeMap: Record<ExceededLimitType, string> = {
      [ExceededLimitType.RecordLimit]: "record limit",
      [ExceededLimitType.AccountTransactionLimit]: "account transaction limit",
      [ExceededLimitType.DataSourceTransactionLimit]:
        "data source transaction limit",
    };

    const limitType = limitTypeMap[error.data.limitType];

    if (importMethod === ImportMethod.File) {
      return {
        title: `File import exceeds the ${limitType}`,
        description: `Your file/s contain a total of ${actualCount} rows, which exceeds the limit of ${maxCountAllowed} rows. Please import one file at a time and split into smaller files if necessary.`,
      };
    }

    if (importMethod === ImportMethod.Manual) {
      return {
        title: `Exceeded the ${limitType}`,
        description: `Adding this transaction would exceed the ${limitType}.`,
      };
    }

    if (importMethod === ImportMethod.Sync) {
      return {
        title: `Sync exceeds the ${limitType}`,
        description: `The sync contains ${actualCount} records, which would exceed the ${limitType}.`,
      };
    }

    assertNever(importMethod);
  }

  return undefined;
}

function parseOtherSylaApiError(error: unknown) {
  if (isSylaApiError(error)) {
    if (error.code === ProblemCode.UserAlreadyHasAccess) {
      return {
        description: "User already has access to this account.",
      };
    } else if (error.code === ProblemCode.UserInviteNotFound) {
      return {
        description: "The user invite no longer exists.",
      };
    } else if (error.code === ProblemCode.AccountMustHaveOwner) {
      return {
        description: "The account must have at least one Owner remaining.",
      };
    } else if (error.code === ProblemCode.CannotDeleteLastAccount) {
      return {
        description: "You cannot delete your last account.",
      };
    } else if (error.code === ProblemCode.Forbidden) {
      return {
        description: "You don't have access to do this.",
      };
    } else if (error.code === ProblemCode.SubscriptionAlreadyExists) {
      return {
        description:
          "There is already an active subscription for this account.",
      };
    } else if (error.code === ProblemCode.UserHasNoPassword) {
      return {
        description:
          "There is no password on this account. Please reset by logging out and using the forgot password.",
      };
    } else if (error.code === ProblemCode.IncorrectPassword) {
      return {
        description: "The password you entered is incorrect.",
      };
    } else if (error.code === ProblemCode.SamePassword) {
      return {
        description:
          "The new password can't be the same as the current password.",
      };
    } else if (error.code === ProblemCode.EmailDisallowed) {
      return {
        description: "The provided email can't be used to create an account.",
      };
    } else if (error.code === ProblemCode.UserEmailNotFound) {
      return {
        description: "No user exists with that email.",
      };
    } else if (error.code === ProblemCode.UnsupportedFileType) {
      return {
        description: "The file type uploaded is not supported.",
      };
    } else if (error.code === ProblemCode.FailedFileImport) {
      return {
        description:
          "The file could not be imported. Contact support for assistance.",
      };
    }
  }

  return undefined;
}

function parseLegacyFallback(error: unknown) {
  // This is a legacy fallback as a number of API endpoints still return an
  // error containing a string message intended for display to the user.
  if (axios.isAxiosError(error) && typeof error.response?.data === "string") {
    return {
      description: error.response.data,
    };
  }

  return undefined;
}

const DefaultUserErrorMessage = {
  description: UnexpectedErrorString,
};
